// 报告查看器 / Report viewer feature.
// 端口自 legacy 的 enterReportViewerMode / loadReportViewer / renderMarkdownReport /
// setReportView；使用 /api/v1/reports/{id} 拉取内容并渲染。
// openReport 统一由 run-control 导出，避免重复定义。
// Ports legacy report-viewer logic, routed at /api/v1/reports/{id}.
// openReport lives in run-control to avoid duplicate definitions.

import { $maybe, escapeHtml, setHidden } from "@/lib/dom";
import { log } from "@/lib/logging";
import { renderMarkdownToSafeHtml, stripMarkdownFrontmatter } from "@/lib/markdown";
import { subscribeReportUpdates } from "@/features/report-broadcast";
import { reportsApi as realReportsApi } from "@/api/reports";
import type { ReportPreviewRequest, ReportPreviewResponse } from "@/api/reports";
import { buildApiUrl } from "@/api/client";
import { demosApi as realDemosApi } from "@/api/demos";
import { graphApi } from "@/api/graph";
import { createLoadingBlock } from "@/lib/spinner";
import { renderGraphErrorInto } from "@/features/graph-viewer/index";
import type { GraphExplorerHandle } from "@/features/graph-viewer/view";
import "@/vendor/md2pdf-report-viewer/md2pdf-report-viewer.js";

type ReportsApiShape = Pick<typeof realReportsApi, "get" | "list" | "preview">;
type DemoReportsApiShape = Pick<
  typeof realDemosApi,
  "getReport" | "demoReportDownloadUrl" | "preview"
>;
type ReportPreviewApiShape = {
  preview: (id: string, body: ReportPreviewRequest) => Promise<ReportPreviewResponse>;
};
type Md2PdfRendererInput = { markdown?: string; theme?: string; reportMode?: string };
type Md2PdfViewerElement = HTMLElement & {
  markdown?: string;
  outlineHeadings?: ReportOutlineHeading[];
  renderer?: (input: Md2PdfRendererInput) => Promise<ReportPreviewResponse>;
  setMarkdown?: (markdown: string, options?: { render?: boolean }) => void;
  setOutlineHeadings?: (headings: ReportOutlineHeading[]) => void;
  renderNow?: () => Promise<void>;
};
type ReportOutlineCandidate = { level: number; text: string };
type ReportOutlineHeading = ReportOutlineCandidate & { id: string };
type ReportOutlineNode = ReportOutlineHeading & { children: ReportOutlineHeading[] };

const REPORT_OUTLINE_SCROLL_OFFSET = 84;
const REPORT_COVER_OUTLINE_ID = "report-cover";
// preview 渲染超时上限，超过即降级 iframe / Preview render deadline before
// falling back to the cached-HTML iframe.
const PREVIEW_RENDER_TIMEOUT_MS = 30_000;
let currentOutlineHeadings: ReportOutlineHeading[] = [];
// 当前报告关联的 simulation 与 graph 懒加载状态。
// Current report's simulation id and lazy graph-pane state.
let currentSimulationId: string | null = null;
let graphPaneLoaded = false;
// 持有 graph 句柄以便换报告时销毁 RAF/interval / Hold the graph handle so
// switching reports tears down its RAF/interval resources.
let graphHandle: GraphExplorerHandle | null = null;

/**
 * 进入报告查看模式：切换根 DOM + 读取路径上的 id + 绑定视图切换。
 * Enter the report-viewer mode: toggle roots, read id from path, bind view-mode buttons.
 */
export function enterReportViewerMode(opts: { reportsApi?: ReportsApiShape } = {}): void {
  setHidden("console-root", true);
  setHidden("report-root", false);
  bindReportEvents();
  const reportId = getReportIdFromPath();
  // 订阅跨 tab regenerate 事件：当前查看的 report 被重新生成时跳转到新 URL / Jump to the new report URL when the currently viewed report has been regenerated.
  subscribeReportUpdates((msg) => {
    if (msg.oldReportId === reportId) {
      window.location.replace(`/reports/${encodeURIComponent(msg.newReportId)}`);
    }
  });
  void loadReportViewer(reportId, opts);
}

/**
 * 进入报告列表模式 / Enter the report-list mode.
 */
export function enterReportListMode(opts: { reportsApi?: ReportsApiShape } = {}): void {
  setHidden("console-root", true);
  setHidden("report-root", false);
  bindReportEvents();
  void loadReportList(opts);
}

/**
 * 进入公开 Demo 报告查看模式 / Enter public demo-report viewer mode.
 */
export function enterDemoReportViewerMode(
  opts: { demosApi?: DemoReportsApiShape } = {},
): void {
  setHidden("console-root", true);
  setHidden("report-root", false);
  bindReportEvents();
  const slug = getDemoReportSlugFromPath();
  void loadDemoReportViewer(slug, opts);
}

/**
 * 绑定 rendered / source / graph 三个切换按钮 / Bind the rendered/source/graph view toggles.
 */
export function bindReportEvents(): void {
  const rendered = $maybe("rendered-toggle");
  const source = $maybe("source-toggle");
  rendered?.addEventListener("click", () => setReportView("rendered"));
  source?.addEventListener("click", () => setReportView("source"));
  const graphToggle = $maybe<HTMLButtonElement>("graph-toggle");
  graphToggle?.addEventListener("click", () => {
    if (!graphToggle.disabled) setReportView("graph");
  });
  bindReportOutlineEvents();
  disableHtmlPlaceholder();
  disablePdfPlaceholder();
}

/**
 * 读取并渲染报告 / Fetch a report by id and render it.
 */
export async function loadReportViewer(
  reportId: string,
  opts: { reportsApi?: ReportsApiShape } = {},
): Promise<void> {
  const reportsApi = opts.reportsApi ?? realReportsApi;
  if (!reportId) {
    renderReportError("Missing report id");
    configureGraphToggle(null);
    return;
  }

  renderReportLoading("Loading report…");
  try {
    const body = await reportsApi.get(reportId);
    const markdown = body.markdown ?? "";
    const titleNode = $maybe("report-title");
    if (titleNode) titleNode.textContent = body.title || body.report_id || "Ripple Report";
    const metaNode = $maybe("report-meta");
    if (metaNode) metaNode.textContent = `${body.mode || "report"} / ${body.created_at || ""}`;
    const downloadLink = $maybe<HTMLAnchorElement>("download-markdown");
    if (downloadLink) {
      downloadLink.href =
        buildApiUrl(
          body.download_urls?.markdown ?? `/api/v1/reports/${encodeURIComponent(reportId)}/download.md`,
        );
    }
    configureDownloadLinks(reportId, body);
    configureGraphToggle(body.simulation_id ?? null);
    if (body.schema_version === "deep-v1") {
      renderDeepReport(reportId, body, reportsApi);
      setReportView("rendered");
    } else {
      renderMarkdownReport(markdown);
      disablePdfPlaceholder();
      setReportView("source");
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    renderReportError(message);
    configureGraphToggle(null);
    setReportView("source");
    log.warn("report load failed", { reportId, message });
  }
}

/**
 * 读取并渲染公开 Demo 报告 / Fetch and render a public demo report.
 */
export async function loadDemoReportViewer(
  slug: string,
  opts: { demosApi?: DemoReportsApiShape } = {},
): Promise<void> {
  const demosApi = opts.demosApi ?? realDemosApi;
  if (!slug) {
    renderReportError("Missing demo report slug");
    return;
  }

  renderReportLoading("Loading demo report…");
  try {
    const body = await demosApi.getReport(slug);
    const item = body.item;
    const markdown = item.markdown ?? "";
    const titleNode = $maybe("report-title");
    if (titleNode) titleNode.textContent = item.title || item.slug || "Demo Report";
    const metaNode = $maybe("report-meta");
    if (metaNode) {
      metaNode.textContent = `demo report / ${item.demo_simulation_slug || item.slug}`;
    }
    const downloadLink = $maybe<HTMLAnchorElement>("download-markdown");
    if (downloadLink) {
      downloadLink.href = item.download_urls?.markdown
        ? buildApiUrl(item.download_urls.markdown)
        : demosApi.demoReportDownloadUrl(slug);
    }
    const detailBody = {
      markdown,
      download_urls: item.download_urls ?? {
        markdown: `/api/v1/demo-reports/${encodeURIComponent(slug)}/download.md`,
      },
    };
    configureDownloadLinks(slug, detailBody);
    // demo 报告不提供 graph / Demo reports never expose the graph pane.
    configureGraphToggle(null);
    if (item.schema_version === "deep-v1") {
      renderDeepReport(slug, detailBody, demosApi);
      setReportView("rendered");
    } else {
      renderMarkdownReport(markdown);
      disablePdfPlaceholder();
      setReportView("source");
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    renderReportError(message);
    configureGraphToggle(null);
    setReportView("source");
    log.warn("demo report load failed", { slug, message });
  }
}

/**
 * 读取并渲染报告列表 / Fetch and render the current user's report list.
 */
export async function loadReportList(
  opts: { reportsApi?: ReportsApiShape } = {},
): Promise<void> {
  const reportsApi = opts.reportsApi ?? realReportsApi;
  renderReportLoading("Loading reports…");
  try {
    const body = await reportsApi.list();
    const titleNode = $maybe("report-title");
    if (titleNode) titleNode.textContent = "Reports";
    const metaNode = $maybe("report-meta");
    if (metaNode) metaNode.textContent = `${body.reports.length} report(s)`;
    const rendered = $maybe("report-rendered");
    const source = $maybe("report-source");
    clearReportOutline();
    // 列表页不提供 graph / The report-list page never exposes the graph pane.
    configureGraphToggle(null);
    if (source) source.textContent = "";
    if (!rendered) return;
    if (!body.reports.length) {
      rendered.innerHTML = `<p>No reports yet.</p>`;
      return;
    }
    rendered.innerHTML = `
      <div class="report-list">
        ${body.reports
          .map(
            (report) => `
              <a class="report-list-item" href="/reports/${encodeURIComponent(report.id)}">
                <strong>${escapeHtml(report.title || report.id)}</strong>
                <span>${escapeHtml(report.mode)} / ${escapeHtml(report.created_at)}</span>
              </a>
            `,
          )
          .join("")}
      </div>
    `;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    renderReportError(message);
    configureGraphToggle(null);
    log.warn("report list failed", { message });
  }
}

/**
 * 渲染 Markdown 到 report-rendered / report-source 两个容器。
 * Render markdown into both the rendered + source panes.
 */
export function renderMarkdownReport(markdown: string): void {
  const rendered = $maybe("report-rendered");
  if (rendered) rendered.innerHTML = renderMarkdownToSafeHtml(markdown);
  const source = $maybe("report-source");
  if (source) source.textContent = markdown;
  const outlineHeadings = renderReportOutlineFromMarkdown(markdown);
  if (rendered) applyOutlineIdsToDocument(rendered, outlineHeadings);
}

/**
 * 渲染 deep-v1 报告组件 / Render a deep-v1 report component.
 */
export function renderDeepReport(
  reportId: string,
  body: { markdown?: string; download_urls?: { html?: string } },
  reportsApi: ReportPreviewApiShape = realReportsApi,
): void {
  const markdown = body.markdown ?? "";
  const rendered = $maybe("report-rendered");
  const markdownOutlineHeadings = renderReportOutlineFromMarkdown(markdown);
  if (rendered) {
    const loading = createLoadingBlock("Rendering report…");
    loading.classList.add("report-preview-loading");
    const viewer = document.createElement("md2pdf-report-viewer") as Md2PdfViewerElement;
    viewer.className = "report-viewer-component";
    viewer.setAttribute("view", "preview");
    viewer.setAttribute("theme", "monocle");
    viewer.setAttribute("report-mode", "auto");
    viewer.setAttribute("controls", "false");
    viewer.setAttribute("debounce-ms", "0");
    viewer.setAttribute("aria-label", "Rendered PMF report");
    viewer.hidden = true;
    if (typeof viewer.setOutlineHeadings === "function") {
      viewer.setOutlineHeadings(markdownOutlineHeadings);
    } else {
      viewer.outlineHeadings = markdownOutlineHeadings;
    }
    let fallbackRendered = false;
    let renderSettled = false;
    // 超时降级：preview 接口迟迟不返回时切换到缓存 HTML iframe，
    // 避免 "Rendering report…" 永久转圈 /
    // Timeout fallback: switch to the cached-HTML iframe when the preview
    // call stalls, so the loading state can never spin forever.
    const fallbackToIframe = () => {
      if (fallbackRendered) return;
      fallbackRendered = true;
      renderDeepReportIframe(rendered, reportId, body);
    };
    const renderTimeoutId = window.setTimeout(() => {
      if (!renderSettled) fallbackToIframe();
    }, PREVIEW_RENDER_TIMEOUT_MS);
    viewer.renderer = async (input: Md2PdfRendererInput): Promise<ReportPreviewResponse> => {
      if ((input.markdown ?? "") !== markdown) {
        throw new Error("Stale report preview ignored");
      }
      return reportsApi.preview(reportId, {
        theme: input.theme || "monocle",
        reportMode: input.reportMode || "auto",
      });
    };
    viewer.addEventListener("md2pdf-render-start", () => {
      loading.hidden = false;
      viewer.hidden = true;
    });
    viewer.addEventListener("md2pdf-render-complete", () => {
      renderSettled = true;
      window.clearTimeout(renderTimeoutId);
      // 超时已降级 iframe 时丢弃迟到的渲染结果，避免双重内容 /
      // Discard the late result if the timeout already swapped in the iframe.
      if (fallbackRendered) return;
      loading.remove();
      viewer.hidden = false;
      window.requestAnimationFrame(() => {
        applyDeepReportOutlineTargets(viewer, markdownOutlineHeadings);
      });
    });
    viewer.addEventListener("md2pdf-render-error", () => {
      renderSettled = true;
      window.clearTimeout(renderTimeoutId);
      fallbackToIframe();
    });
    rendered.replaceChildren(loading, viewer);
    if (typeof viewer.setMarkdown === "function") {
      viewer.setMarkdown(markdown, { render: true });
    } else {
      viewer.markdown = markdown;
      void viewer.renderNow?.();
    }
  }
  const source = $maybe("report-source");
  if (source) source.textContent = markdown;
}

// 回退到 HTML iframe / Fall back to the cached HTML iframe.
function renderDeepReportIframe(
  rendered: HTMLElement,
  reportId: string,
  body: { download_urls?: { html?: string } },
): void {
  const iframe = document.createElement("iframe");
  iframe.className = "report-frame";
  iframe.title = "Rendered PMF report";
  iframe.setAttribute("sandbox", "allow-scripts");
  iframe.addEventListener("load", () => {
    applyOutlineIdsToIframe(iframe, currentOutlineHeadings);
  });
  iframe.src = buildApiUrl(
    body.download_urls?.html ?? `/api/v1/reports/${encodeURIComponent(reportId)}/html`,
  );
  rendered.replaceChildren(iframe);
}

/**
 * 切换 rendered / source / graph 视图 / Toggle among rendered, source and graph views.
 */
export function setReportView(view: "rendered" | "source" | "graph"): void {
  setHidden("report-rendered", view !== "rendered");
  setHidden("report-source", view !== "source");
  setHidden("report-graph", view !== "graph");
  $maybe("rendered-toggle")?.classList.toggle("active", view === "rendered");
  $maybe("source-toggle")?.classList.toggle("active", view === "source");
  $maybe("graph-toggle")?.classList.toggle("active", view === "graph");
  if (view === "graph") void ensureGraphPane();
}

// 绑定报告目录的委托事件 / Bind delegated events for the report outline.
function bindReportOutlineEvents(): void {
  const outline = $maybe("report-outline");
  if (!outline || outline.dataset.bound === "true") return;
  outline.dataset.bound = "true";
  outline.addEventListener("click", onReportOutlineClick);
}

// 处理目录展开折叠与导航点击 / Handle outline collapse and navigation clicks.
function onReportOutlineClick(event: MouseEvent): void {
  const target = event.target instanceof Element ? event.target : null;
  const toggle = target?.closest<HTMLButtonElement>("[data-outline-toggle]");
  if (toggle) {
    const group = toggle.closest<HTMLElement>(".report-outline-group");
    const collapsed = group?.dataset.collapsed === "true";
    if (group) group.dataset.collapsed = String(!collapsed);
    toggle.setAttribute("aria-expanded", String(collapsed));
    toggle.setAttribute("aria-label", collapsed ? "Collapse outline group" : "Expand outline group");
    toggle.textContent = collapsed ? "▾" : "▸";
    return;
  }

  const link = target?.closest<HTMLButtonElement>("[data-outline-target]");
  const outlineTarget = link?.dataset.outlineTarget;
  if (!outlineTarget) return;
  if (outlineTarget === REPORT_COVER_OUTLINE_ID) {
    scrollToReportCover();
    return;
  }
  scrollToReportOutlineTarget(outlineTarget);
}

// 从 Markdown 构建并渲染报告目录 / Build and render the report outline from Markdown.
function renderReportOutlineFromMarkdown(markdown: string): ReportOutlineHeading[] {
  return renderReportOutline(buildOutlineHeadingsFromMarkdown(markdown));
}

// 渲染报告目录，返回已规范化的标题列表 / Render the outline and return normalized headings.
function renderReportOutline(headings: ReportOutlineHeading[]): ReportOutlineHeading[] {
  currentOutlineHeadings = headings;
  const outline = $maybe("report-outline");
  if (!outline) return headings;
  outline.removeAttribute("aria-busy");
  outline.replaceChildren();

  const nav = document.createElement("nav");
  nav.className = "report-outline-nav";
  nav.setAttribute("aria-label", "Report outline");

  const title = document.createElement("div");
  title.className = "report-outline-title";
  title.textContent = "Report Outline";

  const list = document.createElement("div");
  list.className = "report-outline-list";
  list.append(createCoverOutlineGroup());

  for (const node of buildOutlineTree(headings)) {
    list.append(createOutlineGroup(node));
  }

  nav.append(title, list);
  outline.append(nav);
  outline.hidden = false;
  return headings;
}

// 创建固定封面目录项 / Create the fixed cover outline item.
function createCoverOutlineGroup(): HTMLElement {
  const group = document.createElement("section");
  group.className = "report-outline-group";
  group.dataset.collapsed = "false";

  const row = document.createElement("div");
  row.className = "report-outline-row";
  const spacer = document.createElement("span");
  spacer.className = "report-outline-spacer";
  spacer.setAttribute("aria-hidden", "true");

  const button = document.createElement("button");
  button.type = "button";
  button.className = "report-outline-link report-outline-link-root";
  button.dataset.outlineTarget = REPORT_COVER_OUTLINE_ID;
  button.textContent = "Cover";

  row.append(spacer, button);
  group.append(row);
  return group;
}

// 渲染目录加载态 / Render the report-outline loading state.
function renderReportOutlineLoading(): void {
  const outline = $maybe("report-outline");
  if (!outline) return;
  currentOutlineHeadings = [];
  const title = document.createElement("div");
  title.className = "report-outline-title";
  title.textContent = "Report Outline";
  const loading = createLoadingBlock("Loading outline...");
  loading.classList.add("report-outline-loading");
  outline.setAttribute("aria-busy", "true");
  outline.replaceChildren(title, loading);
  outline.hidden = false;
}

// 清空并隐藏报告目录 / Clear and hide the report outline.
function clearReportOutline(): void {
  currentOutlineHeadings = [];
  const outline = $maybe("report-outline");
  if (!outline) return;
  outline.removeAttribute("aria-busy");
  outline.replaceChildren();
  outline.hidden = true;
}

// 创建一个一级目录分组 / Create one top-level outline group.
function createOutlineGroup(node: ReportOutlineNode): HTMLElement {
  const group = document.createElement("section");
  group.className = "report-outline-group";
  group.dataset.collapsed = "false";

  const row = document.createElement("div");
  row.className = "report-outline-row";

  if (node.children.length) {
    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "report-outline-toggle";
    toggle.dataset.outlineToggle = node.id;
    toggle.setAttribute("aria-expanded", "true");
    toggle.setAttribute("aria-label", "Collapse outline group");
    toggle.textContent = "▾";
    row.append(toggle);
  } else {
    const spacer = document.createElement("span");
    spacer.className = "report-outline-spacer";
    spacer.setAttribute("aria-hidden", "true");
    row.append(spacer);
  }

  row.append(createOutlineLink(node, "report-outline-link-root"));
  group.append(row);

  if (node.children.length) {
    const children = document.createElement("div");
    children.className = "report-outline-children";
    for (const child of node.children) {
      children.append(createOutlineLink(child, "report-outline-link-child"));
    }
    group.append(children);
  }

  return group;
}

// 创建目录项按钮 / Create one outline item button.
function createOutlineLink(
  heading: ReportOutlineHeading,
  className: "report-outline-link-root" | "report-outline-link-child",
): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = `report-outline-link ${className}`;
  button.dataset.outlineTarget = heading.id;
  button.textContent = heading.text;
  return button;
}

// 构建最多两层的目录树 / Build an outline tree with at most two levels.
function buildOutlineTree(headings: ReportOutlineHeading[]): ReportOutlineNode[] {
  const firstLevel = headings[0]?.level;
  const roots: ReportOutlineNode[] = [];
  let activeRoot: ReportOutlineNode | null = null;

  for (const heading of headings) {
    if (!firstLevel || heading.level === firstLevel || !activeRoot) {
      activeRoot = { ...heading, children: [] };
      roots.push(activeRoot);
      continue;
    }
    activeRoot.children.push(heading);
  }

  return roots;
}

// 从 Markdown 提取最多两层标题 / Extract up to two heading levels from Markdown.
function buildOutlineHeadingsFromMarkdown(markdown: string): ReportOutlineHeading[] {
  const source = stripMarkdownFrontmatter(markdown);
  const candidates: ReportOutlineCandidate[] = [];
  let fenceChar = "";

  for (const line of source.split(/\r?\n/)) {
    if (fenceChar) {
      if (new RegExp(`^ {0,3}\\${fenceChar}{3,}`).test(line)) fenceChar = "";
      continue;
    }

    const fenceMatch = line.match(/^ {0,3}([`~]){3,}/);
    if (fenceMatch?.[1]) {
      fenceChar = fenceMatch[1];
      continue;
    }

    const headingMatch = line.match(/^ {0,3}(#{1,6})\s+(.+?)\s*$/);
    if (!headingMatch?.[1] || !headingMatch[2]) continue;
    const text = cleanOutlineHeadingText(headingMatch[2]);
    if (text) candidates.push({ level: headingMatch[1].length, text });
  }

  return selectOutlineLevels(dropLeadingDocumentTitle(candidates));
}

// 选择文档中出现的前两级标题 / Select the first two heading levels present in the document.
function selectOutlineLevels(candidates: ReportOutlineCandidate[]): ReportOutlineHeading[] {
  const levels = Array.from(new Set(candidates.map((heading) => heading.level))).sort((a, b) => a - b);
  const firstLevel = levels[0];
  const secondLevel = levels.find((level) => firstLevel !== undefined && level > firstLevel);
  return candidates
    .filter((heading) => heading.level === firstLevel || heading.level === secondLevel)
    .map((heading, index) => ({ ...heading, id: `report-heading-${index}` }));
}

// 剔除唯一的文档封面标题，让目录从正文 section 开始。
// Drop the single cover/title heading so the outline starts at body sections.
function dropLeadingDocumentTitle(candidates: ReportOutlineCandidate[]): ReportOutlineCandidate[] {
  if (candidates.length < 2) return candidates;
  const lowestLevel = Math.min(...candidates.map((heading) => heading.level));
  const lowestLevelCount = candidates.filter((heading) => heading.level === lowestLevel).length;
  if (lowestLevelCount !== 1 || candidates[0]?.level !== lowestLevel) return candidates;
  return candidates.slice(1);
}

// 清理目录标题文本 / Clean heading text for outline labels.
function cleanOutlineHeadingText(value: string): string {
  const withoutClosingHashes = value.trim().replace(/\s+#+\s*$/, "");
  const withoutMarkdown = withoutClosingHashes
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/<[^>]+>/g, "")
    .replace(/\\([\\`*_[\]{}()#+\-.!|>])/g, "$1")
    .replace(/[`*_~]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  const decoder = document.createElement("textarea");
  decoder.innerHTML = withoutMarkdown;
  return decoder.value.trim();
}

// 给当前渲染出的标题挂上目录定位 id / Attach outline target ids to rendered headings.
function applyOutlineIdsToDocument(root: ParentNode, headings: ReportOutlineHeading[]): void {
  if (!headings.length) return;
  const levels = Array.from(new Set(headings.map((heading) => heading.level)));
  const selector = levels.map((level) => `h${level}`).join(", ");
  const nodes = Array.from(root.querySelectorAll<HTMLElement>(selector));
  headings.forEach((heading, index) => {
    const node =
      nodes.find(
        (candidate) =>
          !candidate.dataset.reportOutlineId &&
          cleanOutlineHeadingText(candidate.textContent ?? "") === heading.text,
      ) ?? nodes.find((candidate) => !candidate.dataset.reportOutlineId) ?? nodes[index];
    if (!node) return;
    node.id = heading.id;
    node.dataset.reportOutlineId = heading.id;
  });
}

// 给 deep-v1 iframe 内标题挂上目录定位 id / Attach outline target ids inside a deep-v1 iframe.
function applyDeepReportOutlineTargets(
  viewer: Md2PdfViewerElement,
  headings: ReportOutlineHeading[],
): void {
  const iframe = viewer.shadowRoot?.querySelector<HTMLIFrameElement>("iframe.preview");
  if (!iframe) return;
  applyOutlineIdsToIframe(iframe, headings);
}

// 尝试给 iframe 文档标题挂 id / Try to attach outline ids inside an iframe document.
function applyOutlineIdsToIframe(
  iframe: HTMLIFrameElement,
  headings: ReportOutlineHeading[],
): boolean {
  try {
    const body = iframe.contentDocument?.body;
    if (!body) return false;
    applyOutlineIdsToDocument(body, headings);
    return true;
  } catch {
    return false;
  }
}

// 跳转到目录目标 / Navigate to an outline target.
function scrollToReportOutlineTarget(outlineTarget: string): void {
  setReportView("rendered");
  setActiveReportOutlineItem(outlineTarget);
  window.requestAnimationFrame(() => {
    const rendered = $maybe("report-rendered");
    if (!rendered) return;

    const directTarget = findOutlineTarget(rendered, outlineTarget);
    if (directTarget) {
      scrollWindowToElement(directTarget);
      return;
    }

    const viewer = rendered.querySelector<Md2PdfViewerElement>("md2pdf-report-viewer.report-viewer-component");
    if (viewer && scrollDeepReportTarget(viewer, outlineTarget)) return;

    const iframe = rendered.querySelector<HTMLIFrameElement>("iframe.report-frame");
    if (iframe) scrollIframeTarget(iframe, outlineTarget);
  });
}

// 跳转到报告封面 / Navigate to the rendered report cover.
function scrollToReportCover(): void {
  setReportView("rendered");
  setActiveReportOutlineItem(REPORT_COVER_OUTLINE_ID);
  window.requestAnimationFrame(() => {
    const rendered = $maybe("report-rendered");
    if (rendered) scrollWindowToElement(rendered);
  });
}

// 设置目录当前项状态 / Mark the active outline item.
function setActiveReportOutlineItem(outlineTarget: string): void {
  const outline = $maybe("report-outline");
  if (!outline) return;
  outline.querySelectorAll<HTMLButtonElement>("[data-outline-target]").forEach((button) => {
    const active = button.dataset.outlineTarget === outlineTarget;
    button.classList.toggle("active", active);
    if (active) {
      button.setAttribute("aria-current", "location");
    } else {
      button.removeAttribute("aria-current");
    }
  });
}

// 查找已挂载的目录目标节点 / Find a rendered node tagged for an outline item.
function findOutlineTarget(root: ParentNode, outlineTarget: string): HTMLElement | null {
  return (
    Array.from(root.querySelectorAll<HTMLElement>("[data-report-outline-id]")).find(
      (node) => node.dataset.reportOutlineId === outlineTarget,
    ) ?? null
  );
}

// 滚动主窗口到目标节点 / Scroll the main window to a target element.
function scrollWindowToElement(element: HTMLElement): void {
  const top = Math.max(0, window.scrollY + element.getBoundingClientRect().top - REPORT_OUTLINE_SCROLL_OFFSET);
  try {
    window.scrollTo({ top, behavior: "smooth" });
  } catch {
    element.scrollIntoView?.({ block: "start" });
  }
}

// 滚动到 deep-v1 iframe 内的目录目标 / Scroll to a target inside the deep-v1 preview iframe.
function scrollDeepReportTarget(viewer: Md2PdfViewerElement, outlineTarget: string): boolean {
  const iframe = viewer.shadowRoot?.querySelector<HTMLIFrameElement>("iframe.preview");
  if (!iframe) return false;
  if (scrollIframeTarget(iframe, outlineTarget)) return true;
  return requestDeepReportOutlineOffset(iframe, outlineTarget);
}

// 滚动到 iframe 内的目录目标 / Scroll to a target inside an iframe.
function scrollIframeTarget(iframe: HTMLIFrameElement, outlineTarget: string): boolean {
  try {
    const body = iframe.contentDocument?.body;
    if (!body) return false;
    const target = findOutlineTarget(body, outlineTarget);
    if (!target) return false;
    const frameRect = iframe.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const top = Math.max(0, window.scrollY + frameRect.top + targetRect.top - REPORT_OUTLINE_SCROLL_OFFSET);
    try {
      window.scrollTo({ top, behavior: "smooth" });
    } catch {
      target.scrollIntoView?.({ block: "start" });
    }
    return true;
  } catch {
    return false;
  }
}

// 通过 postMessage 请求 sandbox iframe 回传目标标题偏移。
// Ask the sandboxed iframe for a target heading offset via postMessage.
function requestDeepReportOutlineOffset(iframe: HTMLIFrameElement, outlineTarget: string): boolean {
  const frameWindow = iframe.contentWindow;
  if (!frameWindow) return false;
  const requestId = `report-outline-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const timeoutId = window.setTimeout(() => {
    window.removeEventListener("message", onMessage);
  }, 1200);

  function onMessage(event: MessageEvent): void {
    if (event.source !== frameWindow) return;
    const data = event.data;
    if (
      !data ||
      data.type !== "md2pdf:outline-offset" ||
      data.requestId !== requestId ||
      data.targetId !== outlineTarget
    ) {
      return;
    }
    window.clearTimeout(timeoutId);
    window.removeEventListener("message", onMessage);
    const offsetTop = Number(data.offsetTop);
    if (!Number.isFinite(offsetTop)) return;
    const frameTop = iframe.getBoundingClientRect().top;
    const top = Math.max(0, window.scrollY + frameTop + offsetTop - REPORT_OUTLINE_SCROLL_OFFSET);
    try {
      window.scrollTo({ top, behavior: "smooth" });
    } catch {
      iframe.scrollIntoView?.({ block: "start" });
    }
  }

  window.addEventListener("message", onMessage);
  frameWindow.postMessage(
    {
      type: "md2pdf:outline-offset-request",
      targetId: outlineTarget,
      requestId,
    },
    "*",
  );
  return true;
}

/**
 * 判断当前 URL 是否属于报告路径 / Whether the current URL targets the report viewer.
 */
export function isReportPath(): boolean {
  return /^\/reports\/[^/]+/.test(window.location.pathname) && !isReportListPath();
}

/**
 * 判断当前 URL 是否属于报告列表 / Whether the current URL targets the report list.
 */
export function isReportListPath(): boolean {
  return window.location.pathname === "/reports" || window.location.pathname === "/reports/list";
}

/**
 * 判断当前 URL 是否属于公开 Demo 报告路径 / Whether the current URL targets a public demo report.
 */
export function isDemoReportPath(): boolean {
  return /^\/demo-reports\/[^/]+/.test(window.location.pathname);
}

// 从 pathname 抽取 reportId / Extract reportId from the current pathname.
function getReportIdFromPath(): string {
  const match = window.location.pathname.match(/^\/reports\/([^/]+)/);
  return match && match[1] ? decodeURIComponent(match[1]) : "";
}

// 从 pathname 抽取 demo report slug / Extract demo report slug from the current pathname.
function getDemoReportSlugFromPath(): string {
  const match = window.location.pathname.match(/^\/demo-reports\/([^/]+)/);
  return match && match[1] ? decodeURIComponent(match[1]) : "";
}

// 渲染加载中态 / Render a loading-state report view.
// 在 fetch 前立即调用，避免首屏空白。
// Called synchronously before every report fetch so the pane is never blank.
function renderReportLoading(label: string): void {
  const title = $maybe("report-title");
  if (title) title.textContent = "Loading…";
  const meta = $maybe("report-meta");
  if (meta) meta.textContent = "";
  const rendered = $maybe("report-rendered");
  if (rendered) {
    rendered.replaceChildren(createLoadingBlock(label));
  }
  const source = $maybe("report-source");
  if (source) source.textContent = "";
  renderReportOutlineLoading();
  disableHtmlPlaceholder();
  disablePdfPlaceholder();
}

// 渲染错误态 / Render an error-state report view.
function renderReportError(message: string): void {
  const title = $maybe("report-title");
  if (title) title.textContent = "Report unavailable";
  const meta = $maybe("report-meta");
  if (meta) meta.textContent = message;
  const rendered = $maybe("report-rendered");
  if (rendered) rendered.textContent = message;
  const source = $maybe("report-source");
  if (source) source.textContent = "";
  clearReportOutline();
}

// 配置 graph 切换按钮 / Configure the graph toggle for the current report.
function configureGraphToggle(simulationId: string | null): void {
  // 先销毁上一份 graph 实例，释放定时器 / 观察器等资源。
  // Destroy the previous graph instance first to release timers/observers.
  graphHandle?.destroy();
  graphHandle = null;
  currentSimulationId = simulationId;
  graphPaneLoaded = false;
  const pane = $maybe("report-graph");
  if (pane) pane.replaceChildren();
  const toggle = $maybe<HTMLButtonElement>("graph-toggle");
  if (!toggle) return;
  if (simulationId) {
    toggle.disabled = false;
    toggle.title = "Graph view";
  } else {
    toggle.disabled = true;
    toggle.title = "This report has no linked simulation";
  }
}

// 首次切到 graph 时懒加载渲染；后续切换复用实例。
// Lazy-load the graph pane on first switch; later switches reuse the instance.
async function ensureGraphPane(): Promise<void> {
  if (graphPaneLoaded || !currentSimulationId) return;
  const pane = $maybe("report-graph");
  if (!pane) return;
  graphPaneLoaded = true;
  pane.replaceChildren(createLoadingBlock("Loading graph…"));
  try {
    // 仅 view 模块保持动态 import（force-graph chunk 的闸门）；graphApi /
    // renderGraphErrorInto 已在主包，改用顶部静态 import。
    // Only the view module stays dynamic (the force-graph chunk gate);
    // graphApi / renderGraphErrorInto are already in the main bundle.
    const { mountGraphExplorer } = await import("@/features/graph-viewer/view");
    const payload = await graphApi.get(currentSimulationId);
    pane.replaceChildren();
    graphHandle = await mountGraphExplorer(pane, payload);
  } catch (error) {
    graphPaneLoaded = false; // 失败后允许重试 / Allow retry after failure.
    renderGraphErrorInto(pane, error);
  }
}

// 配置报告下载链接 / Configure report download links.
function configureDownloadLinks(
  reportId: string,
  body: { download_urls?: { markdown?: string; html?: string; pdf?: string } },
): void {
  const markdownLink = $maybe<HTMLAnchorElement>("download-markdown");
  if (markdownLink) {
    markdownLink.href = buildApiUrl(
      body.download_urls?.markdown ?? `/api/v1/reports/${encodeURIComponent(reportId)}/download.md`,
    );
  }
  const htmlLink = $maybe<HTMLAnchorElement>("download-html");
  if (htmlLink && body.download_urls?.html) {
    htmlLink.href = buildApiUrl(body.download_urls.html);
    htmlLink.removeAttribute("aria-disabled");
    htmlLink.classList.remove("disabled");
  } else {
    disableHtmlPlaceholder();
  }
  const pdfLink = $maybe<HTMLAnchorElement>("download-pdf");
  if (pdfLink && body.download_urls?.pdf) {
    pdfLink.href = buildApiUrl(body.download_urls.pdf);
    pdfLink.removeAttribute("aria-disabled");
    pdfLink.classList.remove("disabled");
    pdfLink.title = "Download PDF";
  } else {
    disablePdfPlaceholder();
  }
}

// HTML 导出占位 / Disable HTML export when the report has no rich HTML endpoint.
function disableHtmlPlaceholder(): void {
  const htmlLink = $maybe<HTMLAnchorElement>("download-html");
  if (!htmlLink) return;
  htmlLink.removeAttribute("href");
  htmlLink.setAttribute("aria-disabled", "true");
  htmlLink.classList.add("disabled");
}

// PDF 导出占位 / Disable PDF export when the report has no PDF endpoint.
function disablePdfPlaceholder(): void {
  const pdfLink = $maybe<HTMLAnchorElement>("download-pdf");
  if (!pdfLink) return;
  pdfLink.removeAttribute("href");
  pdfLink.setAttribute("aria-disabled", "true");
  pdfLink.classList.add("disabled");
  pdfLink.title = "PDF export is not available for this report";
}
